import React, { useEffect } from 'react';
import SingleSelect from "components/SingleSelect";
import SearchSelect from "../../../../components/SearchSelect";
import { useTmsSearch } from '../../hooks/useTmsSearch';
import { useFilteredCarriers, isCarrierValidForRouteType } from '../../hooks/useFilteredCarriers';
import { TmsCarrierType } from '../../route/graphql/queries/getTmsCarriers';
import { TmsRouteTypeEnum } from '../../../../enums/TmsEnum';
import { t } from '../../../../i18n';
import { getSmartLabelClassName } from '../../../../utils/labelWidthUtils';

export interface FilterOptions {
    scheduleId: string;
    routeId: string;
    routeType: string;
    carrierId: string;
    status: string;
}

export const DEFAULT_FILTER_OPTIONS: FilterOptions = {
    scheduleId: '',
    routeId: '',
    routeType: '',
    carrierId: '',
    status: ''
};

interface ScheduleFilterProps {
    statusOptions: { label: string; value: number }[];
    routeTypeOptions: { label: string; value: number }[];
    carriersData: TmsCarrierType[] | undefined;
    handoverCarrierOptions: { label: string; value: string }[];
    isHandoverTab?: boolean;
    currentRouteType?: string; // 当前 tab 选中的 routeType
    filters: FilterOptions;
    setFilters: (filters: FilterOptions) => void;
}

const ScheduleFilter: React.FC<ScheduleFilterProps> = ({ statusOptions, routeTypeOptions, carriersData, handoverCarrierOptions, isHandoverTab, currentRouteType, filters, setFilters }) => {
    const { handleRouteSearch, handleScheduleSearch } = useTmsSearch();

    // 同步 currentRouteType 到本地 filters.routeType
    useEffect(() => {
        if (currentRouteType !== undefined) {
            // 只有当 currentRouteType 与当前值不同时才更新
            if (filters.routeType !== currentRouteType) {
                // 切换 tab 时清空路线选项、班次选项和承运商选项
                setFilters({ ...filters, routeType: currentRouteType, scheduleId: '', routeId: '', carrierId: '' });
            }
        }
    }, [currentRouteType, filters, setFilters]);

    // Filter carriers based on routeType using the reusable hook
    // 优先使用 currentRouteType，如果没有则使用 filters.routeType
    const effectiveRouteType = currentRouteType || filters.routeType;
    const carrierOptions = useFilteredCarriers(carriersData, effectiveRouteType);

    const handleFilterChange = (name: string, value: any) => {
        let processedValue = value;
        if (typeof value === 'string' && value.trim() === '') {
            processedValue = undefined;
        }
        
        // Special handling for routeType change: check if current carrierId is still valid
        if (name === 'routeType' && value !== undefined && carriersData && filters.carrierId) {
            const isValid = isCarrierValidForRouteType(carriersData, filters.carrierId, value);
            if (!isValid) {
                // Clear carrierId if it's no longer valid
                setFilters({ ...filters, [name]: processedValue, carrierId: '' });
                return;
            }
        }
        
        setFilters({ ...filters, [name]: processedValue });
    };

    // 三列布局：按列最宽对齐
    // Row1: schedule | route | carrier
    // Row2: status   | (empty) | (empty)
    const labelSchedule = String(t('tms.schedule.filters.schedule'));
    const labelRoute = String(t('tms.schedule.filters.route'));
    const labelCarrier = String(isHandoverTab ? t('tms.schedule.drawer.handoverCarrier') : t('tms.schedule.filters.carrier'));
    const labelStatus = String(t('tms.schedule.filters.status'));

    const column1Labels = [labelSchedule, labelStatus];
    const column2Labels = [labelRoute];
    const column3Labels = [labelCarrier];

    return (
        <div className="mb-2 pl-0 py-2">
            {/* 使用 Grid 布局确保列对齐 */}
            <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                <div className="min-w-0">
                    <SearchSelect
                        label={labelSchedule}
                        placeholder={t('tms.schedule.filters.searchPlaceholder')}
                        value={filters.scheduleId}
                        onChange={(value) => {
                            handleFilterChange('scheduleId', value);
                        }}
                        onSearch={(searchValue, callback) => {
                            // 根据当前 tab 的 routeType 过滤路线搜索
                            const routeType = currentRouteType ? Number(currentRouteType) : undefined;
                            handleScheduleSearch(searchValue, callback, routeType);
                        }}
                        spanClassName={getSmartLabelClassName(column1Labels)}
                    />
                </div>
                <div className="min-w-0">
                    <SearchSelect
                        label={labelRoute}
                        placeholder={t('tms.schedule.filters.searchPlaceholder')}
                        value={filters.routeId}
                        onChange={(value) => {
                            handleFilterChange('routeId', value);
                        }}
                        onSearch={(searchValue, callback) => {
                            // 根据当前 tab 的 routeType 过滤路线搜索
                            const routeType = currentRouteType ? Number(currentRouteType) : undefined;
                            handleRouteSearch(searchValue, callback, undefined, false, routeType);
                        }}
                        spanClassName={getSmartLabelClassName(column2Labels)}
                    />
                </div>
                <div className="min-w-0">
                    <SingleSelect
                        label={labelCarrier}
                        value={filters.carrierId}
                        onChange={value => handleFilterChange('carrierId', value)}
                        options={isHandoverTab ? handoverCarrierOptions : carrierOptions}
                        spanClassName={getSmartLabelClassName(column3Labels)}
                    />
                </div>
                {/* 第二行 */}
                <div className="min-w-0">
                    <SingleSelect
                        label={labelStatus}
                        value={filters.status}
                        onChange={value => handleFilterChange('status', value)}
                        options={statusOptions}
                        spanClassName={getSmartLabelClassName(column1Labels)}
                    />
                </div>
                <div className="min-w-0"></div>
                <div className="min-w-0"></div>
            </div>
        </div>
    );
};

export default ScheduleFilter;

